import React, { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, reloadForStaleBuild } from '../utils/selfHeal';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, retry: () => void) => ReactNode);
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Set when the error was a missing chunk from an older build. */
  staleBuild?: 'reloading' | 'failed';
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, staleBuild: undefined };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Report to Sentry (production only)
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo.componentStack } },
        tags: { errorBoundary: true },
      });
    }

    if (isChunkLoadError(error)) {
      // One guarded reload to pick up the new build; if we already tried in the
      // last 30s, stay on the error screen instead of looping.
      const reloading = reloadForStaleBuild();
      this.setState({ staleBuild: reloading ? 'reloading' : 'failed' });
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, staleBuild: undefined });
  };

  handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleGoHome = (): void => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(this.state.error as Error, this.handleRetry);
        }
        return this.props.fallback;
      }

      const { staleBuild } = this.state;
      const title = staleBuild === 'reloading'
        ? 'Updating to the latest version…'
        : staleBuild === 'failed'
          ? 'A new version of the portal is available'
          : 'Something went wrong';
      const message = staleBuild === 'reloading'
        ? 'The portal was updated while this tab was open. Reloading now.'
        : staleBuild === 'failed'
          ? "We couldn't load part of the new version automatically. Reload the page to continue; if it keeps happening, check your connection."
          : 'An unexpected error occurred. Please try reloading the page.';

      return (
        <div role="alert" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: 'var(--color-text-primary, #1a1a1a)' }}>
            {title}
          </h1>
          <p style={{ color: 'var(--color-text-secondary, #666)', marginBottom: '1.5rem', maxWidth: '400px' }}>
            {message}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: 'var(--color-primary, #2196F3)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Reload Page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: 'transparent',
                color: 'var(--color-primary, #2196F3)',
                border: '1px solid var(--color-primary, #2196F3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Go to Home
            </button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <pre style={{
              marginTop: '2rem',
              padding: '1rem',
              backgroundColor: 'var(--color-background-alt, #f5f5f5)',
              borderRadius: '6px',
              fontSize: '0.75rem',
              color: 'var(--color-error, #d32f2f)',
              maxWidth: '600px',
              overflow: 'auto',
              textAlign: 'left'
            }}>
              {this.state.error.toString()}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
